import RestUtility from '../../restUtility';
import {
  ClientSubscription,
  SubscriptionCancelServerResponse,
  ClientSubscriptionProduct,
  SubscriptionProductsServerResponse,
  SubscriptionSkipDeliveryServerResponse,
  SubscriptionsServerResponse,
  SubscriptionStatus,
  UpdateSubscriptionData,
  SubscriptionDetailResponse,
  SubscriptionDetail,
} from 'types/Subscription.types';
import { subscriptionEndpoint } from '../../restUtility/restEndpoints';
import { notifyError } from 'config/setupBugsnag';

export class SubscriptionService {
  private restUtility: RestUtility;

  constructor() {
    this.restUtility = new RestUtility();
  }

  async getSubscriptionHistory(pageParam?: number, itemPerPage = 600) {
    try {
      const urlParams = `page=${pageParam}&per_page=${itemPerPage}`;

      const response = await this.restUtility.get<SubscriptionsServerResponse>(
        `${subscriptionEndpoint}?${urlParams}`
      );

      return this.buildSubscriptionHistoryForClient(response, pageParam);
    } catch (error) {
      notifyError('ApiError:: getSubscriptionHistory', error);
      throw error;
    }
  }

  async getSubscriptionProducts(itemPerPage = 200) {
    try {
      const urlParams = `per_page=${itemPerPage}`;

      const response =
        await this.restUtility.get<SubscriptionProductsServerResponse>(
          `${subscriptionEndpoint}/get_products?${urlParams}`
        );

      return this.buildSubscriptionProductsForClient(response);
    } catch (error) {
      notifyError('ApiError:: getSubscriptionProducts', error);
      throw error;
    }
  }

  async updateSubscriptionFrequencyAndQuantity(
    subscriptionId: number,
    subscriptionFrequencyId: number,
    quantity: number
  ) {
    try {
      const requestData: {
        subscription_frequency_id?: number;
        quantity?: number;
      } = {};
      if (subscriptionFrequencyId === 0 && quantity === 0) {
        throw new Error('Frequency and quantity cannot both be zero');
      }
      if (subscriptionFrequencyId !== 0) {
        requestData.subscription_frequency_id = subscriptionFrequencyId;
      }
      if (quantity !== 0) {
        requestData.quantity = quantity;
      }

      const response = await this.restUtility.put<SubscriptionsServerResponse>(
        `${subscriptionEndpoint}/${subscriptionId}`,
        {
          subscription: requestData,
        }
      );
      return response;
    } catch (error) {
      notifyError('ApiError:: updateSubscriptionFrequency', error);
      throw error;
    }
  }

  async updateSubscriptionAddress(
    subscriptionId: number,
    shipAddressId: number,
    billAddressId: number
  ) {
    try {
      const response = await this.restUtility.put<SubscriptionsServerResponse>(
        `${subscriptionEndpoint}/${subscriptionId}`,
        {
          subscription: {
            ship_address_id: shipAddressId,
            bill_address_id: billAddressId,
          },
        }
      );
      return response;
    } catch (error) {
      notifyError('ApiError:: updateSubscriptionShippingAddress', error);
      throw error;
    }
  }

  async updateSubscriptionPayment(
    subscriptionId: number,
    cardToken: string | null
  ) {
    try {
      const response = await this.restUtility.put<SubscriptionsServerResponse>(
        `${subscriptionEndpoint}/${subscriptionId}`,
        {
          subscription: { card_token: cardToken },
        }
      );
      return response;
    } catch (error) {
      notifyError('ApiError:: updateSubscriptionPayment', error);
      throw error;
    }
  }

  async updateSubscriptionNextDelivery(
    subscriptionId: number,
    nextOccurrenceAt: string,
    isPermanentChange: boolean
  ) {
    try {
      const response = await this.restUtility.put<SubscriptionsServerResponse>(
        `${subscriptionEndpoint}/${subscriptionId}`,
        {
          subscription: {
            next_occurrence_at: nextOccurrenceAt,
            is_permanent_change: isPermanentChange,
          },
        }
      );
      return response;
    } catch (error) {
      notifyError('ApiError:: updateSubscriptionNextDelivery', error);
      throw error;
    }
  }

  async cancelSubscription(subscriptionId: number, cancelReason: string) {
    try {
      const response =
        await this.restUtility.put<SubscriptionCancelServerResponse>(
          `${subscriptionEndpoint}/${subscriptionId}/cancel`,
          {
            subscription: { cancellation_reasons: cancelReason },
          }
        );
      return response;
    } catch (error) {
      notifyError('ApiError:: cancelSubscription', error);
      throw error;
    }
  }

  async skipSubscriptionDelivery(subscriptionId: number) {
    try {
      const response =
        await this.restUtility.put<SubscriptionSkipDeliveryServerResponse>(
          `${subscriptionEndpoint}/${subscriptionId}/skip_delivery`,
          {}
        );
      return response;
    } catch (error) {
      notifyError('ApiError:: skipSubscriptionDelivery', error);
      throw error;
    }
  }

  async subscriptionDetail(subscriptionId: number) {
    try {
      const response = await this.restUtility.get<SubscriptionDetailResponse>(
        `${subscriptionEndpoint}/${subscriptionId}/orders`
      );
      return this.buildSubscriptionDetail(response);
    } catch (error) {
      notifyError('ApiError:: subscriptionDetail', error);
      throw error;
    }
  }

  buildSubscriptionProductsForClient(
    response: SubscriptionProductsServerResponse
  ): ClientSubscriptionProduct[] {
    if (!response?.data?.subscription_products) {
      return [];
    }
    return response.data.subscription_products.map(subscription => ({
      sku: subscription.sku,
      options: subscription.options.map(option => ({
        quantity: option.quantity,
        frequencies: option.frequencies.map(frequency => ({
          id: frequency.id,
          displayText: frequency.display_text,
          discount: frequency.discount,
          originalPrice: frequency.original_price,
          discountedPrice: frequency.discounted_price,
        })),
      })),
    }));
  }

  buildSubscriptionHistoryForClient(
    response: SubscriptionsServerResponse,
    currentPageNumber?: number
  ) {
    const noimage = '/images/noimage.jpeg';
    const clientSubscriptionHistory: {
      subscriptions: ClientSubscription[];
      count: number;
      totalCount: number;
      totalPages: number;
      currentPageNumber?: number;
    } = {
      subscriptions: [],
      count: 0,
      totalCount: 0,
      totalPages: 0,
      currentPageNumber: currentPageNumber,
    };

    if (!response || !response.data || !response.data.subscriptions) {
      return clientSubscriptionHistory;
    }

    response.data.subscriptions.forEach(subscription =>
      clientSubscriptionHistory.subscriptions.push({
        id: subscription.id,
        price: subscription.price,
        nextOccurrenceAt: subscription.next_occurrence_at,
        lastShipmentDate: subscription.last_shipment_date,
        variantSku: subscription.variant_sku,
        productName: subscription.product_name,
        imageUrl: subscription.image_url || noimage,
        frequency: subscription.frequency,
        frequencyId: subscription.frequency_id,
        shipAddressId: subscription.ship_address_id,
        billAddressId: subscription.bill_address_id,
        deliveryDateAfterSkip: subscription.delivery_date_after_skip,
        quantity: subscription.quantity,
        cardToken: subscription.card_token,
        status: subscription.status as SubscriptionStatus,
        lastDayToChangeShipment: subscription.last_day_to_change_shipment,
        discountPercent: subscription.discount_percent,
        suspendedReason: subscription.suspended_reason,
        subscriptionNumber: subscription.number,
      })
    );

    clientSubscriptionHistory.count = response.data.meta.count;
    clientSubscriptionHistory.totalCount = response.data.meta.total_count;
    clientSubscriptionHistory.totalPages = response.data.meta.total_pages;

    return clientSubscriptionHistory;
  }

  buildSubscriptionDetail(
    response: SubscriptionDetailResponse
  ): SubscriptionDetail {
    const subscriptionDetail: SubscriptionDetail = {
      subscriptionNumber: '',
      status: '',
      orders: [],
    };

    if (!response) {
      return subscriptionDetail;
    }

    subscriptionDetail.subscriptionNumber = response.number;
    subscriptionDetail.status = response.status;
    subscriptionDetail.orders = response.orders.map(order => ({
      id: order.id,
      number: order.number,
      state: order.state,
      total: order.total,
      createdAt: order.created_at,
      sku: order.sku,
      quantity: order.quantity,
      frequency: order.frequency,
      discount: order.discount,
      price: order.price,
      paymentType: order.payment_type,
      nextOccuranceDate: order.next_occurance_date,
      subscriptionOrderType: order.subscription_order_type,
      billAddressId: order.bill_address_id,
      shipAddressId: order.ship_address_id,
      trackingNumber: order.tracking_number,
      trackingUrl: order.tracking_url,
      status: order.status as SubscriptionStatus,
    }));

    return subscriptionDetail;
  }
}
